/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/scope */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../core/components/NavBar";
import UserContext from "../core/UserContext";
import currencyFormatter from "currency-formatter";
import "./Application.css";
import Moment from "moment";
import {
  getUserDataFromLocalStorage,
  getPolicyClassName,
  log,
} from "../core/utils";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { getLives } from "../quotation-view/QuotationView";
import apiRoutes from "../api/apiRoutes";
import UsePagination from "../core/components/usePagination";
import { RiErrorWarningFill } from "react-icons/ri";

const FlexiApplication = () => {
  const { userData } = useContext(UserContext);
  const userDataStorage = getUserDataFromLocalStorage();
  const axiosPrivate = useAxiosPrivate(userDataStorage);
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingQuotation, setDownloadingQuotation] = useState([]);
  const [nullData, setNullData] = useState("");
  const [paginatedLimit, setPaginatedLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const primaryAction = async (application) => {
    if (application.status === "underwriting") {
      navigate(`/underwriting/${application.id}/main-life`);
    } else if (application.status === "kyc") {
      navigate(`/client-kyc/${application?.user?.id}/${application.id}/main`);
    } else if (application.status === "review") {
      navigate(`/review/${application.id}`);
    } else if (application.status === "firstpayment") {
      navigate(`/quotations/${application.id}/first-payment`);
    }
  };

  const getApplications = useCallback(async () => {
    const url = process.env.REACT_APP_API_URL + apiRoutes.flexiApplications;
    setLoading(true);
    try {
      const response = await axiosPrivate.get(url, {
        params: {
          pageSize: paginatedLimit,
          page: currentPage,
        },
      });

      setLoading(false);
      if (response.data.success === true) {
        const filteredApplications = response.data.data
          .filter(
            (item) => item.status !== "initial" && item.status !== "accepted",
          )
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

        const filteredTotal = filteredApplications.length;
        setTotalItems(filteredTotal);
        setTotalPages(Math.ceil(filteredTotal / paginatedLimit));

        const downloadingSpinners = [];
        const processedApplications = filteredApplications.map(
          (application, index) => {
            downloadingSpinners.push({ index, status: false });
            application.quotationFormData = JSON.parse(
              application.quotationFormData,
            );
            application.lives = getLives(application);
            return application;
          },
        );

        setDownloadingQuotation(downloadingSpinners);
        setApplications(processedApplications);

        if (processedApplications.length < 1) {
          setNullData(
            <div className="tw-py-10 tw-px-4 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4">
              <h6>No Data Available</h6>
              <span className="tw-text-7xl">&#x1F4C2;</span>
            </div>,
          );
        } else {
          setNullData("");
        }
      }
    } catch (error) {
      setLoading(false);
      setNullData(
        <div className="tw-py-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-1">
          <div className="tw-items-center tw-flex tw-flex-col">
            <RiErrorWarningFill className="tw-text-red-500 tw-text-[36px]" />
            <h5>Error loading applications</h5>
          </div>
          <p>{error.message}</p>
        </div>,
      );
      log(error);
    }
  }, [paginatedLimit, currentPage, axiosPrivate]);

  const getQuotationLinkAndDownload = async (_quotation, index) => {
    const url =
      process.env.REACT_APP_API_URL + apiRoutes.downloadPdf + _quotation.id;
    try {
      const updatedSpinners = [...downloadingQuotation];
      updatedSpinners[index] = { index, status: true };
      setDownloadingQuotation(updatedSpinners);

      const response = await axiosPrivate.get(url);
      if (response.data.success === true) {
        const pdfData = response.data.data.buffer;
        const byteCharacters = atob(pdfData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        const updatedSpinners = [...downloadingQuotation];
        updatedSpinners[index] = { index, status: false };
        setDownloadingQuotation(updatedSpinners);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "quotation.pdf");
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } else {
        const updatedSpinners = [...downloadingQuotation];
        updatedSpinners[index] = { index, status: false };
        setDownloadingQuotation(updatedSpinners);
      }
    } catch (error) {
      const updatedSpinners = [...downloadingQuotation];
      updatedSpinners[index] = { index, status: false };
      setDownloadingQuotation(updatedSpinners);
      console.error("Error downloading quotation:", error);
    }
  };

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit) => {
    setPaginatedLimit(Number(newLimit));
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    getApplications();
  }, [getApplications]);

  return (
    <>
      <Navbar
        name={
          userData
            ? `${userData?.data?.firstname} ${userData?.data?.lastname}`
            : ""
        }
      />
      <div className="containerx tw-px-3 sm:tw-px-5 tw-w-full tw-max-w-7xl tw-mx-auto">
        <div
          className="mt-4 d-flex justify-content-between align-items-center p-4 px-5 text-white ila-bg"
          style={{ borderRadius: 2 }}
        >
          <h4 className="mb-0 tw-text-xl tw-font-semibold">Applications</h4>
        </div>

        {loading && (
          <div
            style={{ marginTop: 150 }}
            className="d-flex flex-column justify-content-center align-items-center"
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div>Please wait...</div>
          </div>
        )}

        {nullData && <div className="tw-py-10">{nullData}</div>}

        {applications && !loading && !nullData && (
          <div className="tw-w-full tw-overflow-x-auto">
            <table
              style={{ padding: "20px !important" }}
              className="table tw-table-auto tw-whitespace-nowrap tw-min-w-full"
            >
              <thead className="tw-font-bold tw-text-black">
                <tr>
                  <th scope="col">Temporal ID</th>
                  <th scope="col">Contract Parties</th>
                  <th scope="col">Product</th>
                  <th scope="col">Sum Assured</th>
                  <th scope="col">Savings Type</th>
                  <th scope="col">Premium</th>
                  <th scope="col">Status</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody className="tw-text-black">
                {applications.map((application, index) => {
                  const isDownloading =
                    downloadingQuotation.find((item) => item.index === index)
                      ?.status || false;

                  return (
                    <tr key={index} className="tw-capitalize">
                      <td scope="row">{application.temporaryId}</td>
                      <td>{application.lives}</td>
                      <td>
                        {getPolicyClassName(
                          application,
                          application.quotationFormData.loan_plan,
                        )}
                      </td>
                      <td>
                        {currencyFormatter.format(
                          parseFloat(
                            application.quotationFormData.initial_sum_assured,
                          ),
                          { symbol: "Rs " },
                        )}
                      </td>
                      <td>{application?.savingsType}</td>
                      <td>
                        {currencyFormatter.format(
                          parseFloat(application?.premiumAmount),
                          { symbol: "Rs " },
                        )}
                      </td>
                      <td>{application.status}</td>
                      <td>
                        <a
                          className="btn btn-sm ila-action-link"
                          onClick={() => {
                            getQuotationLinkAndDownload(application, index);
                          }}
                        >
                          {isDownloading && (
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          )}
                          Download
                        </a>
                        &nbsp;
                        <a
                          className="btn btn-sm ila-action-link"
                          onClick={() => {
                            if (!application.paymentStatus) {
                              primaryAction(application);
                            } else {
                              navigate("/application-in-review");
                            }
                          }}
                        >
                          Continue
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <UsePagination
              pgdrop="tw-hidden"
              limit={paginatedLimit}
              total={totalItems}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default FlexiApplication;
